import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
const CSRF_COOKIE_NAME = "aurion_csrf";

export const api = axios.create({
  baseURL: API,
  timeout: 45000,
  withCredentials: true,
});

const readCookie = (name) => {
  if (typeof document === "undefined") return null;
  const prefix = `${name}=`;
  const parts = document.cookie ? document.cookie.split("; ") : [];
  for (const p of parts) {
    if (p.startsWith(prefix)) return decodeURIComponent(p.slice(prefix.length));
  }
  return null;
};

let cachedCsrf = null;
export const setCachedCsrf = (t) => {
  cachedCsrf = t || null;
};

api.interceptors.request.use((cfg) => {
  const mutating = /^(post|put|patch|delete)$/i.test(cfg.method || "");
  if (mutating) {
    const token = readCookie(CSRF_COOKIE_NAME) || cachedCsrf;
    if (token) cfg.headers["X-CSRF-Token"] = token;
  }
  return cfg;
});

let onUnauth = null;
export const setUnauthHandler = (fn) => {
  onUnauth = fn;
};
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      // Mark as silent so downstream `.catch` handlers can skip toasts —
      // the global onUnauth handler already redirects to /login, and any
      // additional "Not authenticated" toast on that page is pure noise.
      // Login/register submits carry their own error handling that reads
      // the response body directly, so this flag doesn't hide real
      // wrong-credential errors from the login form.
      err.silent = true;
      if (onUnauth) onUnauth();
    }
    return Promise.reject(err);
  },
);

// --- Auth --------------------------------------------------------------
export const apiRegister = (payload) =>
  api.post(`/auth/register`, payload).then((r) => {
    if (r.data?.csrf_token) setCachedCsrf(r.data.csrf_token);
    return r.data;
  });
export const apiLogin = (payload) =>
  api.post(`/auth/login`, payload).then((r) => {
    if (r.data?.csrf_token) setCachedCsrf(r.data.csrf_token);
    return r.data;
  });
export const apiLogout = () =>
  api.post(`/auth/logout`).then((r) => {
    setCachedCsrf(null);
    return r.data;
  });
export const apiMe = () => api.get(`/auth/me`).then((r) => r.data);
export const apiChangePassword = (payload) =>
  api.post(`/auth/change-password`, payload).then((r) => {
    if (r.data?.csrf_token) setCachedCsrf(r.data.csrf_token);
    return r.data;
  });
export const apiRefreshCsrf = () =>
  api.get(`/auth/csrf`).then((r) => {
    if (r.data?.csrf_token) setCachedCsrf(r.data.csrf_token);
    return r.data;
  });

// --- Market ------------------------------------------------------------
export const getQuote = (symbol) =>
  api.get(`/market/quote`, { params: { symbol } }).then((r) => r.data);
export const getHistory = (symbol, period = "1mo", interval = "1d") =>
  api.get(`/market/history`, { params: { symbol, period, interval } }).then((r) => r.data);

// --- Analysis ----------------------------------------------------------
export const startAnalysis = (symbol, portfolioId = null) =>
  api
    .post(`/analysis/run`, portfolioId ? { symbol, portfolio_id: portfolioId } : { symbol })
    .then((r) => r.data);
export const getAnalysis = (runId) =>
  api.get(`/analysis/${runId}`).then((r) => r.data);
export const listMyRuns = () => api.get(`/analysis`).then((r) => r.data);

// --- Portfolios --------------------------------------------------------
export const listPortfolios = () => api.get(`/portfolios`).then((r) => r.data);
export const createPortfolio = (payload) =>
  api.post(`/portfolios`, payload).then((r) => r.data);
export const getPortfolio = (id) =>
  api.get(`/portfolios/${id}`).then((r) => r.data);
export const renamePortfolio = (id, name) =>
  api.patch(`/portfolios/${id}`, { name }).then((r) => r.data);
export const deletePortfolio = (id) =>
  api.delete(`/portfolios/${id}`).then((r) => r.data);
export const getAggregate = () => api.get(`/portfolios/aggregate`).then((r) => r.data);

// --- Desk (Phase 3) ----------------------------------------------------
export const getEvents = (portfolioId, limit = 100) =>
  api.get(`/portfolios/${portfolioId}/events`, { params: { limit } }).then((r) => r.data);
export const getSnapshots = (portfolioId, limit = 200) =>
  api.get(`/portfolios/${portfolioId}/snapshots`, { params: { limit } }).then((r) => r.data);
export const getRisk = (portfolioId) =>
  api.get(`/portfolios/${portfolioId}/risk`).then((r) => r.data);
export const getSettings = (portfolioId) =>
  api.get(`/portfolios/${portfolioId}/settings`).then((r) => r.data);
export const patchSettings = (portfolioId, payload) =>
  api.patch(`/portfolios/${portfolioId}/settings`, payload).then((r) => r.data);
export const listProposals = (portfolioId, params = {}) =>
  api.get(`/portfolios/${portfolioId}/proposals`, { params }).then((r) => r.data);
export const approveProposal = (portfolioId, proposalId) =>
  api.post(`/portfolios/${portfolioId}/proposals/${proposalId}/approve`).then((r) => r.data);
export const rejectProposal = (portfolioId, proposalId) =>
  api.post(`/portfolios/${portfolioId}/proposals/${proposalId}/reject`).then((r) => r.data);

// --- Trades ------------------------------------------------------------
export const createTrade = (portfolioId, payload) =>
  api.post(`/portfolios/${portfolioId}/trades`, payload).then((r) => r.data);
export const listTrades = (portfolioId, { limit = 50, offset = 0 } = {}) =>
  api.get(`/portfolios/${portfolioId}/trades`, { params: { limit, offset } }).then((r) => r.data);

// --- Scanner (Phase 6) -------------------------------------------------
export const getScanner = (portfolioId) =>
  api.get(`/portfolios/${portfolioId}/scanner`).then((r) => r.data);
export const patchScanner = (portfolioId, payload) =>
  api.patch(`/portfolios/${portfolioId}/scanner`, payload).then((r) => r.data);
export const scanNow = (portfolioId) =>
  api.post(`/portfolios/${portfolioId}/scanner/run-now`).then((r) => r.data);

// --- Admin -------------------------------------------------------------
export const listInvites = () => api.get(`/admin/invites`).then((r) => r.data);
export const createInvite = (note) =>
  api.post(`/admin/invites`, { note: note || null }).then((r) => r.data);
export const revokeInvite = (id) =>
  api.post(`/admin/invites/${id}/revoke`).then((r) => r.data);
export const listUsers = () => api.get(`/admin/users`).then((r) => r.data);
export const activateUser = (id) =>
  api.post(`/admin/users/${id}/activate`).then((r) => r.data);
export const deactivateUser = (id) =>
  api.post(`/admin/users/${id}/deactivate`).then((r) => r.data);
export const cloakListPortfolios = (userId) =>
  api.get(`/admin/users/${userId}/portfolios`).then((r) => r.data);
export const cloakGetPortfolio = (userId, portfolioId) =>
  api.get(`/admin/users/${userId}/portfolios/${portfolioId}`).then((r) => r.data);
export const listAudit = (params = {}) =>
  api.get(`/admin/audit`, { params }).then((r) => r.data);

// --- Alpaca (Phase 4) --------------------------------------------------
export const alpacaStatus = () => api.get(`/alpaca/status`).then((r) => r.data);
export const alpacaConnect = ({ key_id, secret }) =>
  api.post(`/alpaca/connect`, { key_id, secret }).then((r) => r.data);
export const alpacaDisconnect = () =>
  api.post(`/alpaca/disconnect`).then((r) => r.data);
export const alpacaAccount = () => api.get(`/alpaca/account`).then((r) => r.data);
export const cancelOrder = (portfolioId, orderId) =>
  api.post(`/portfolios/${portfolioId}/orders/${orderId}/cancel`).then((r) => r.data);

// --- Capital.com (Phase 7 — DEMO ONLY) ---------------------------------
export const capitalStatus = () => api.get(`/capital/status`).then((r) => r.data);
export const capitalConnect = ({ identifier, password, api_key }) =>
  api.post(`/capital/connect`, { identifier, password, api_key }).then((r) => r.data);
export const capitalDisconnect = () =>
  api.post(`/capital/disconnect`).then((r) => r.data);
export const capitalAccount = () => api.get(`/capital/account`).then((r) => r.data);
export const capitalPositions = () => api.get(`/capital/positions`).then((r) => r.data);

// --- OANDA v20 (Phase 9 — PRACTICE ONLY) --------------------------------
export const oandaStatus = () => api.get(`/oanda/status`).then((r) => r.data);
export const oandaConnect = ({ token, account_id }) =>
  api.post(`/oanda/connect`, { token, account_id }).then((r) => r.data);
export const oandaDisconnect = () =>
  api.post(`/oanda/disconnect`).then((r) => r.data);
export const oandaAccount = () => api.get(`/oanda/account`).then((r) => r.data);
export const oandaPositions = () => api.get(`/oanda/positions`).then((r) => r.data);

// --- Phase 8: universe / watchlist / discover --------------------------
export const listSymbols = () =>
  api.get(`/market/symbols`).then((r) => r.data);
export const getWatchlist = () =>
  api.get(`/watchlist`).then((r) => r.data);
export const watchlistAdd = (symbol) =>
  api.post(`/watchlist/add`, { symbol }).then((r) => r.data);
export const watchlistRemove = (symbol) =>
  api.post(`/watchlist/remove`, { symbol }).then((r) => r.data);
export const discoverRisersFallers = (limit = 5) =>
  api.get(`/discover/risers-fallers`, { params: { limit } }).then((r) => r.data);
export const discoverVolatile = (limit = 6) =>
  api.get(`/discover/volatile`, { params: { limit } }).then((r) => r.data);
export const discoverMostTraded = () =>
  api.get(`/discover/most-traded`).then((r) => r.data);
export const discoverNews = (limit = 10) =>
  api.get(`/discover/news`, { params: { limit } }).then((r) => r.data);

// --- Telegram (Phase 5) ------------------------------------------------
export const telegramStatus = () => api.get(`/telegram/status`).then((r) => r.data);
export const telegramLinkInit = () =>
  api.post(`/telegram/link`).then((r) => r.data);
export const telegramUnlink = () =>
  api.post(`/telegram/unlink`).then((r) => r.data);
export const telegramPatchPrefs = (updates) =>
  api.patch(`/telegram/prefs`, updates).then((r) => r.data);

// --- Error helper ------------------------------------------------------
export const formatApiError = (e) => {
  const detail = e?.response?.data?.detail;
  if (detail == null) return e?.message || "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((it) => (it && typeof it.msg === "string" ? it.msg : JSON.stringify(it)))
      .filter(Boolean)
      .join(" · ");
  if (detail?.msg) return detail.msg;
  return String(detail);
};

/**
 * Toast an API error UNLESS the error is a background 401 — those are
 * handled by the global `onUnauth` redirect and any "Not authenticated"
 * toast on the login page (or after a session-expiry redirect) is pure
 * noise. Login/register forms should still `toast.error(formatApiError(e))`
 * directly because they explicitly wrap a user-initiated auth call and
 * want to surface "Invalid email or password".
 */
export const toastApiError = (e, fallback) => {
  if (e?.silent || e?.response?.status === 401) return;
  const msg = formatApiError(e) || fallback || "Something went wrong.";
  toast.error(msg);
};
